import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Button, Alert, CircularProgress, Chip, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TablePagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import EmitirSolicitudPermisoDialog from "../components/EmitirSolicitudPermisoDialog";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const ESTADO_COLOR = { Espera: "warning", Emitido: "default", Aceptado: "success", Rechazado: "error" };
const colorEstado = (estado) => ESTADO_COLOR[estado] || "error";

const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const formatFechaHora = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${formatFecha(value)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

// Hook chico para no repetir 2 veces la misma lógica de "listado paginado contra un
// endpoint GET con {page, limit} -> {data, total}" (Vacaciones Emitidas y Solicitudes
// de Permiso usan exactamente el mismo patrón, contra dos tablas distintas).
const useListadoPaginado = (url) => {
  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const cargar = useCallback((targetPage, targetRowsPerPage) => {
    setLoading(true);
    setError("");
    axios.get(url, { params: { page: targetPage + 1, limit: targetRowsPerPage } })
      .then(({ data }) => {
        setLista(data?.data || []);
        setTotal(Number(data?.total || 0));
      })
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(() => {
    cargar(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const reset = useCallback(() => {
    setPage(0);
    cargar(0, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage]);

  return {
    lista, total, loading, error, page, rowsPerPage,
    setPage, setRowsPerPage, reset,
  };
};

// "Solicitudes de Permiso" pidio mostrar nada mas las ultimas 5 sin
// paginacion (a diferencia de Vacaciones Emitidas/Dias Ganados, que siguen
// paginadas) — mismo endpoint, pero con limit fijo y sin controles de pagina.
const useUltimasSolicitudes = (url, cantidad = 5) => {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const cargar = useCallback(() => {
    setLoading(true);
    setError("");
    axios.get(url, { params: { page: 1, limit: cantidad } })
      .then(({ data }) => setLista(data?.data || []))
      .catch((e) => setError(e?.response?.data?.message || "Error al cargar."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, cantidad]);

  useEffect(() => { cargar(); }, [cargar]);

  return { lista, loading, error, reset: cargar };
};

const Paginacion = ({ total, page, rowsPerPage, onPageChange, onRowsPerPageChange }) => (
  <TablePagination
    component="div"
    count={total}
    page={page}
    rowsPerPage={rowsPerPage}
    rowsPerPageOptions={[10, 25, 50]}
    labelRowsPerPage="Filas por página"
    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
    onPageChange={(_, newPage) => onPageChange(newPage)}
    onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
  />
);

const MisSolicitudesPermisoPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [diasDisponibles, setDiasDisponibles] = useState(null);
  const [habilitado, setHabilitado] = useState(true); // true hasta que se sepa que no
  const [soyAprobador, setSoyAprobador] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportando, setExportando] = useState(false);

  const licencias = useListadoPaginado("/api/permisos/licencias-emitidas");
  const solicitudes = useUltimasSolicitudes("/api/permisos/mis-solicitudes", 5);
  const ganados = useListadoPaginado("/api/permisos/historial-ganados");

  useEffect(() => {
    Promise.all([
      axios.get("/api/permisos/dias-disponibles"),
      axios.get("/api/permisos/soy-aprobador"),
    ])
      .then(([resDias, resAprobador]) => {
        setDiasDisponibles(resDias.data?.data?.cantidad ?? null);
        setHabilitado(Boolean(resDias.data?.data?.id_empleado));
        setSoyAprobador(Boolean(resAprobador.data?.data?.esAprobador));
      })
      .catch(() => {});
  }, []);

  const handleExportar = async () => {
    setExportando(true);
    try {
      const response = await axios.get("/api/permisos/exportar", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "mis_permisos.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      // silencioso: exportación secundaria, no bloquea el resto de la pantalla
    } finally {
      setExportando(false);
    }
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Mis Solicitudes de Permiso"
        subtitle="Vacaciones y permisos"
        showBack={false}
      />
      <Box sx={{ p: 1 }}>
        {!habilitado && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Usuario no habilitado para vacaciones. Tu usuario no está vinculado a un registro de personal —
            contacta a Administración de Personal para poder emitir solicitudes.
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          {diasDisponibles !== null && habilitado && (
            <Alert severity="info" sx={{ py: 0.25 }}>
              Días disponibles: <strong>{diasDisponibles}</strong>
            </Alert>
          )}
          <Box sx={{ flex: 1 }} />
          {soyAprobador && (
            <Button size="small" variant="outlined" onClick={() => navigate("/permisos/solicitudes")}>
              Solicitudes pendientes (aprobación)
            </Button>
          )}
          <Button
            size="small" variant="outlined" startIcon={<DownloadIcon />}
            onClick={handleExportar}
            disabled={exportando}
          >
            {exportando ? "Exportando..." : "Descargar Excel"}
          </Button>
          <Button
            size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            disabled={!habilitado}
          >
            Nueva solicitud
          </Button>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, minHeight: 34 }}>
          <Tab label="Vacaciones Emitidas" sx={{ minHeight: 34, py: 0.5 }} />
          <Tab label="Solicitudes de Permiso" sx={{ minHeight: 34, py: 0.5 }} />
          <Tab label="Días Ganados" sx={{ minHeight: 34, py: 0.5 }} />
        </Tabs>

        {tab === 0 && (
          <>
            {licencias.error && <Alert severity="error" sx={{ mb: 1 }}>{licencias.error}</Alert>}
            {licencias.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : licencias.total === 0 ? (
              <Alert severity="info">No tienes vacaciones o permisos emitidos.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>Fecha de Petición</Th>
                        <Th>Razón</Th>
                        <Th>Fecha Inicio</Th>
                        <Th>Fecha Finalización</Th>
                        <Th align="center">Días</Th>
                        <Th>Observación</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {licencias.lista.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{formatFecha(r.fecha_peticion)}</TableCell>
                          <TableCell>{r.razon}</TableCell>
                          <TableCell>{formatFecha(r.fecha_ini)}</TableCell>
                          <TableCell>{formatFecha(r.fecha_fin)}</TableCell>
                          <TableCell align="center">{r.cantidad}</TableCell>
                          <TableCell>{r.observacion || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Paginacion
                  total={licencias.total} page={licencias.page} rowsPerPage={licencias.rowsPerPage}
                  onPageChange={licencias.setPage}
                  onRowsPerPageChange={(n) => { licencias.setRowsPerPage(n); licencias.setPage(0); }}
                />
              </Paper>
            )}
          </>
        )}

        {tab === 1 && (
          <>
            {solicitudes.error && <Alert severity="error" sx={{ mb: 1 }}>{solicitudes.error}</Alert>}
            {solicitudes.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : solicitudes.lista.length === 0 ? (
              <Alert severity="info">No tienes solicitudes registradas.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>ID</Th>
                        <Th>Razón</Th>
                        <Th>Fecha inicio</Th>
                        <Th>Fecha fin</Th>
                        <Th align="center">Días</Th>
                        <Th>Observación</Th>
                        <Th>Registrado</Th>
                        <Th align="center">Estado</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {solicitudes.lista.map((s) => (
                        <TableRow key={s.id} hover>
                          <TableCell>{s.id}</TableCell>
                          <TableCell>{s.razon}</TableCell>
                          <TableCell>{formatFecha(s.fecha_ini)}</TableCell>
                          <TableCell>{formatFecha(s.fecha_fin)}</TableCell>
                          <TableCell align="center">{s.dias}</TableCell>
                          <TableCell>{s.observacion || "-"}</TableCell>
                          <TableCell>{formatFechaHora(s.fecha_registro)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={s.estado}
                              size="small"
                              color={colorEstado(s.estado)}
                              sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </>
        )}
        {tab === 2 && (
          <>
            {ganados.error && <Alert severity="error" sx={{ mb: 1 }}>{ganados.error}</Alert>}
            {ganados.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : ganados.total === 0 ? (
              <Alert severity="info">No tienes historial de días ganados.</Alert>
            ) : (
              <Paper variant="outlined">
                <TableContainer>
                  <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                    <TableHead>
                      <TableRow>
                        <Th>Fecha de Registro</Th>
                        <Th align="center">Gestión</Th>
                        <Th align="center">Días</Th>
                        <Th>Observación</Th>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ganados.lista.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{formatFecha(r.fecha)}</TableCell>
                          <TableCell align="center">{r.year}</TableCell>
                          <TableCell align="center">{r.cantidad}</TableCell>
                          <TableCell>{r.observacion || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Paginacion
                  total={ganados.total} page={ganados.page} rowsPerPage={ganados.rowsPerPage}
                  onPageChange={ganados.setPage}
                  onRowsPerPageChange={(n) => { ganados.setRowsPerPage(n); ganados.setPage(0); }}
                />
              </Paper>
            )}
          </>
        )}
      </Box>

      <EmitirSolicitudPermisoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={() => {
          setDialogOpen(false);
          setTab(1);
          solicitudes.reset();
        }}
      />
    </DashboardLayout>
  );
};

export default MisSolicitudesPermisoPage;
